/**
* @license Apache-2.0
*
* Copyright (c) 2024 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

'use strict';

/*
* When adding modules to the namespace, ensure that they are added in alphabetical order according to module name.
*/

// MODULES //

import setReadOnly from '@stdlib/utils/define-read-only-property';


// MAIN //

/**
* Top-level namespace.
*
* @namespace ns
*/
var ns = {};

/**
* @name add
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/complex/float64/base/add}
*/
import add from './../../../float64/base/add';
setReadOnly( ns, 'add', add );

/**
* @name add3
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/complex/float64/base/add3}
*/
import add3 from './../../../float64/base/add3';
setReadOnly( ns, 'add3', add3 );

/**
* @name assert
* @memberof ns
* @readonly
* @type {Namespace}
* @see {@link module:@stdlib/complex/float64/base/assert}
*/
import assert from './../../../float64/base/assert';
setReadOnly( ns, 'assert', assert );

/**
* @name div
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/complex/float64/base/div}
*/
import div from './../../../float64/base/div';
setReadOnly( ns, 'div', div );

/**
* @name identity
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/complex/float64/base/identity}
*/
import identity from './../../../float64/base/identity';
setReadOnly( ns, 'identity', identity );

/**
* @name mul
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/complex/float64/base/mul}
*/
import mul from './../../../float64/base/mul';
setReadOnly( ns, 'mul', mul );

/**
* @name muladd
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/complex/float64/base/mul-add}
*/
import muladd from './../../../float64/base/mul-add';
setReadOnly( ns, 'muladd', muladd );

/**
* @name neg
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/complex/float64/base/neg}
*/
import neg from './../../../float64/base/neg';
setReadOnly( ns, 'neg', neg );

/**
* @name scale
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/complex/float64/base/scale}
*/
import scale from './../../../float64/base/scale';
setReadOnly( ns, 'scale', scale );

/**
* @name sub
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/complex/float64/base/sub}
*/
import sub from './../../../float64/base/sub';
setReadOnly( ns, 'sub', sub );


// EXPORTS //

export default ns;
